import { BohemiaCodeLoaderConfig } from '@ms/bohemia-codeloader';
import { DriverInfo } from './DriverInfo';
import { HostConfig, OfficeWebHostScope } from './OfficeWebHostTypes';
import { LoadInIframeResult } from './Iframe-shared/LoadInIframeResult';
import { loadComponentInSourcelessIframeInternal } from './Iframe-sourceless/Outside/loadComponentInSourcelessIframeInternal';

export interface SourcelessIframeLoadOptions {
  /**
   * [For internal or testing use only]
   * A code-loader config override. Will take precedence over other code-loader configurations (e.g., over containerEnvironment)
   */
  DoNotUse_codeLoaderConfigOverride?: BohemiaCodeLoaderConfig; // eslint-disable-line @typescript-eslint/naming-convention

  /**
   * Title for iframe element to be read by screen readers.
   */
  iframeTitle: string;

  /**
   * The path to the bundle that will be loaded inside the iframe. This is typically injected via a Webpack DefinePlugin.
   *
   * This parameter is not required yet -- if left empty, we will load from our CDN -- but in a future version of
   * Office Web Host, partners will be required to supply their own. Learn more by searching for `sourcelessIframeBundleUrl`
   * in <https://office.visualstudio.com/OC/_wiki/wikis/Fluid%20Framework%20Experiences/15005/OfficeWebHost>.
   */
  // Task 4883749: When we are ready to bump OWH version, remove the "?​​" from the parameter below to make it required,
  // and also remove the comment in the JSDOC that starts with "This parameter is not required yet" and ends in "supply their own."
  sourcelessIframeBundleUrl?: string;

  /**
   * Sourceless iframes have the ability to know when they are being removed from the DOM and as a result can do any memory cleanups on their own.
   * Exposing a dispose method here if host apps want the iframe to do some custom cleanup on their behalf
   */
  onDispose?: () => void;

  /**
   * Set to `true` if you want the load to pause after doing the initial bootstrapping logic.
   * The caller would then be responsible for calling `.resumeLoad()` on the resulting `LoadInIframeResult` object.
   */
  partialLoadOnly?: boolean;

  /**
   * A flag for whether to use the service-delivered ODSP driver (if relevant) versus a bundled one.
   * This flag, when "true", is used for two purposes:
   * 1. It preps the "Outside" of the iframe for your usage of the ODSP driver, to help optimize the flow.
   * 2. It ensures that the right bundle is loaded on the "Inside" of the iframe *IF* you are using the iframe-inside
   *    bundle from the Fluid CDN.
   *    IMPORTANTLY, if you've already transitioned to hosting your own (by passing the `sourcelessIframeBundleUrl` option),
   *    you will need to ensure that your bundle calls
   *    `loadComponentFromInsideSourcelessIframeWithCdnDrivers` (note the `WithCdnDrivers` suffix)
   *    instead of the original `loadComponentFromInsideSourcelessIframe` API.
   */
  useServiceDeliveredDriver?: boolean;
}

/**
 * Represents the result of calling "loadComponentInSourcelessIframe"
 */
export interface LoadInSourcelessIframeResult extends LoadInIframeResult {
  /**
   * Returns a reference to the iframe object, or undefined if `loadResult.dispose()` has been called
   * or if the iframe has already been unmounted from the DOM.
   */
  getIframe: () => HTMLIFrameElement | undefined;

  /**
   * If `partialLoadOnly` was specified in the `SourcelessIframeLoadOptions`,
   * the `LoadInSourcelessIframeResult` object will contain a `resumeLoad` method
   * by which the rest of the initialization logic can be triggered.
   */
  resumeLoad?: () => void;
}

/**
 * An internal-only interface that extends the "SourcelessIframeLoadOptions" object
 * and exposes an optional "_DoNotUse_ForTestingOnly" property bag.
 */
export interface SourcelessIframeLoadOptionsForTesting extends SourcelessIframeLoadOptions {
  /** Additional options for testing purposes only, NOT intended to be used by anything outside the office-web-host DemoApp */
  _DoNotUse_ForTestingOnly?: SourcelessIframeTestingOnlyOptions; // eslint-disable-line @typescript-eslint/naming-convention
}

/**
 * An internal-only interface representing the "_DoNotUse_ForTestingOnly" property bag on
 * "SourcelessIframeLoadOptionsForTesting"
 */
export interface SourcelessIframeTestingOnlyOptions {
  /**
   * A string of JS code to inject into the iframe (e.g., to be able to test error conditions, etc.).
   * This code is executed after the initialize method on the inside of the iframe has been called
   * (but before the container and component promises have resolved).
   */
  codeToInject?: string;

  /**
   * Only in the case of a "partialLoadOnly" option being passed in:
   * An optional callback that will get triggered immediately after the iframe onload event fires
   */
  afterIframeOnload?: () => void;
}

/**
 * Loads a component within a sourceless iframe, into a provided hosting div,
 * given appropriate information and auth callbacks.
 * Failure to load will result in promise rejection with information
 * about the error.
 * @param driverInfo Driver specific information needed for performing Fluid load operations
 * @param componentDiv A div or a promise to a div that the component will be rendered into
 * @param scope An OfficeWebHostScope object that will be available to all containers/components loaded
 * @param config Additional information allowing a host to have richer integration with how components are initialized
 * @param options The sourceless iframe load options
 */
export function loadComponentInSourcelessIframe(
  driverInfo: DriverInfo,
  componentDiv: HTMLElement,
  scope: OfficeWebHostScope,
  config: HostConfig,
  options: SourcelessIframeLoadOptions
): LoadInSourcelessIframeResult {
  // The moment we should consider the start of office-web-host execution, which is used for end to end performance analysis.
  const bootstrapperStartTime = Date.now();

  return loadComponentInSourcelessIframeInternal({ type: 'Load', driverInfo }, componentDiv, scope, config, options, {
    bootstrapperStartTime
  });
}

// cspell:ignore bootstrapper
